import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CsCardComponent, CsCardInterfaceClass } from 'components/cs-card/cs-card';
import { CsCardListInterfaceClass } from 'components/cs-card-list/cs-card-list-interface';
import { LL } from 'i18n/i18n.service';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { PlanNoteMode, SubscriptionCardModel, SubscriptionTypeLabelKind } from './subscription-card-model';

/**
 * The User Profile page's subscription card.
 *
 * It decides nothing. Every rule about what to show lives in
 * `buildSubscriptionCardModel`, which the page runs and hands over as `model` —
 * the only data this component takes. It acts on nothing either: each button
 * emits, and the page performs the action, because the actions need the alert,
 * loading and navigation controllers, telemetry and the subscription service.
 *
 * ## Why it is OnPush with a single object input, and why that is safe
 *
 * OnPush redraws this component only when an input's identity changes. The page
 * builds a NEW model object in exactly one place (`UserProfilePage.renderCard`),
 * and that is also the only place it marks itself for check — so every change the
 * page draws on purpose reaches this card too. A getter returning a fresh object
 * per render would instead throw ExpressionChangedAfterItHasBeenChecked in
 * development, which is why the model is stored rather than computed on read.
 *
 * What that trades away, on purpose: the model is a snapshot taken at the last
 * rebuild. The old getters re-read the login result and the clock on EVERY page
 * redraw, including one caused by something unrelated like opening the header
 * menu. The card now keeps the snapshot until the page rebuilds it. That matters
 * only for a login result replaced from outside this page, or a coach grant that
 * expires while the page sits open — and the page re-renders on neither by itself
 * today, since it subscribes to nothing. See `UserProfilePage.subscriptionCard`.
 *
 * ## Why it is a "wrapper card"
 *
 * It sits directly inside the page's `cs-card-list`, and that list lays out only
 * its direct children that register as cards. The `cs-card` in this template is a
 * grandchild, so without registering, the list never positioned it: the card was
 * drawn over the Profile card with its buttons unclickable (nightly E2E, 2026-09-13).
 * So this component follows the wrapper pattern documented in `cs-card.ts`, as
 * `CsSupportCardComponent` does: it registers itself and forwards layout and height
 * to the inner card, and its host element takes the shared card base styles from
 * `cs-subscription-card.scss`.
 *
 * The card's content styles still live in `user-profile.scss`, nested under
 * `page-user-profile`: this element always sits inside that page, so those
 * descendant selectors keep matching.
 */
@Component({
  selector: 'cs-subscription-card',
  templateUrl: 'cs-subscription-card.html',
  providers: [{ provide: CsCardInterfaceClass, useExisting: CsSubscriptionCard }],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsSubscriptionCard extends CsCardInterfaceClass implements AfterViewInit {
  protected LL = LL;
  protected PlanNoteMode = PlanNoteMode;

  @Input() model: SubscriptionCardModel;

  @Output() upgradeClick = new EventEmitter<void>();
  @Output() resumeClick = new EventEmitter<void>();
  @Output() manageSubscriptionClick = new EventEmitter<void>();
  @Output() switchPlanClick = new EventEmitter<void>();

  @ViewChild(CsCardComponent) card: CsCardComponent;

  constructor(elemRef: ElementRef) {
    super(elemRef);
  }

  ngAfterViewInit(): void {
    // the list hands itself to this host before the inner card exists, so pass it on now
    this.card?.setList(this.list);
    this.card?.updateLayout();
  }

  // forward the list to the inner card too: its own height changes (the refreshing
  // row, notes appearing) re-stack the list only through the list it holds
  override setList(list: CsCardListInterfaceClass): void {
    super.setList(list);
    this.card?.setList(list);
  }

  override updateLayout(): void {
    this.card?.updateLayout();
  }

  override getCurrentCardHeight(): number {
    return this.card ? this.card.getCurrentCardHeight() : 0;
  }

  /** "Premium - Monthly" for a paid member with a known plan, otherwise the role
   *  name alone. The plan title is a billing-plan name from the payment provider,
   *  so it is data rather than catalog text and is shown as delivered. */
  protected get badgeText(): TranslatedString {
    if (this.model.badgeIncludesPlanTitle) {
      return untranslatedString(`${this.model.roleName} - ${this.model.currentPlan.title}`);
    }
    return this.model.roleName;
  }

  /** The "Subscription type" row's text, or '' when there is no plan. */
  protected get subscriptionTypeLabel(): TranslatedString {
    switch (this.model.subscriptionTypeLabelKind) {
      case SubscriptionTypeLabelKind.Monthly: return LL.profile.subscriptionTypeMonthly();
      case SubscriptionTypeLabelKind.Yearly: return LL.profile.subscriptionTypeYearly();
      case SubscriptionTypeLabelKind.PlanTitle: return untranslatedString(this.model.currentPlan.title);
      case SubscriptionTypeLabelKind.None: return untranslatedString('');
    }
  }
}
